import { z } from 'zod';

/** Schemas da Fase 2: cardápio, mesas, horários, preferências e pedidos. Dinheiro sempre em centavos. */

const text = (min: number, max: number, msg = 'Campo obrigatório') => z.string().trim().min(min, msg).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const cents = z.number().int('Valor inválido').min(0, 'Valor não pode ser negativo').max(10_000_00, 'Valor alto demais');
const minuteOfDay = z.number().int().min(0).max(1440);
/** Caminho de imagem enviada pelo próprio restaurante (validado também no servidor contra o diretório do tenant). */
const imagePath = z
  .string()
  .regex(/^\/uploads\/restaurants\/[0-9a-f-]{36}\/[\w-]+\.(png|jpg|webp)$/, 'Imagem inválida')
  .nullable()
  .optional();

export const categoryInputSchema = z
  .object({
    name: text(1, 60),
    description: optionalText(300),
    imagePath,
    visible: z.boolean().default(true),
    availableFrom: minuteOfDay.nullable().optional(),
    availableTo: minuteOfDay.nullable().optional(),
  })
  .refine((c) => (c.availableFrom == null) === (c.availableTo == null), {
    message: 'Informe início e fim da disponibilidade, ou nenhum dos dois',
    path: ['availableTo'],
  });
export type CategoryInput = z.infer<typeof categoryInputSchema>;

export const productInputSchema = z
  .object({
    categoryId: z.uuid('Escolha a categoria'),
    stationId: z.uuid('Escolha o setor de produção'),
    name: text(1, 80),
    description: optionalText(500),
    imagePath,
    priceCents: cents,
    available: z.boolean().default(true),
    featured: z.boolean().default(false),
    prepMinutes: z.number().int().min(1).max(240).nullable().optional(),
    allowNotes: z.boolean().default(true),
    maxQuantity: z.number().int().min(1).max(99).default(20),
    trackStock: z.boolean().default(false),
    stockQuantity: z.number().int().min(0).max(100_000).nullable().optional(),
    optionGroupIds: z.array(z.uuid()).max(20).default([]),
  })
  .refine((p) => !p.trackStock || p.stockQuantity != null, { message: 'Informe a quantidade em estoque', path: ['stockQuantity'] });
export type ProductInput = z.infer<typeof productInputSchema>;

export const optionInputSchema = z.object({
  id: z.uuid().optional(),
  name: text(1, 60),
  priceCents: cents,
  available: z.boolean().default(true),
});

export const optionGroupInputSchema = z
  .object({
    name: text(1, 60),
    description: optionalText(200),
    minSelect: z.number().int().min(0).max(20),
    maxSelect: z.number().int().min(1).max(20),
    options: z.array(optionInputSchema).min(1, 'Cadastre pelo menos uma opção').max(50),
  })
  .refine((g) => g.minSelect <= g.maxSelect, { message: 'O mínimo não pode ser maior que o máximo', path: ['maxSelect'] })
  .refine((g) => g.minSelect <= g.options.length, { message: 'O mínimo é maior que o número de opções', path: ['minSelect'] });
export type OptionGroupInput = z.infer<typeof optionGroupInputSchema>;

export const reorderSchema = z.object({ ids: z.array(z.uuid()).min(1).max(500) });

export const tableInputSchema = z.object({ label: text(1, 30, 'Informe o número ou nome da mesa') });
export const tableBatchSchema = z
  .object({
    prefix: z.string().trim().max(15).default(''),
    from: z.number().int().min(1).max(999),
    to: z.number().int().min(1).max(999),
  })
  .refine((b) => b.to >= b.from && b.to - b.from < 200, { message: 'Intervalo inválido (até 200 mesas por vez)', path: ['to'] });

export const businessHoursSchema = z.object({
  hours: z
    .array(z.object({ weekday: z.number().int().min(0).max(6), opensAt: minuteOfDay, closesAt: minuteOfDay }))
    .max(28)
    .refine((hs) => hs.every((h) => h.opensAt !== h.closesAt), 'Abertura e fechamento não podem ser iguais'),
});

export const orderSettingsSchema = z.object({
  acceptOrders: z.boolean(),
  enforceHours: z.boolean(),
  autoOpenSessions: z.boolean(),
  menuNotice: optionalText(160),
});

export const orderItemInputSchema = z.object({
  productId: z.uuid(),
  quantity: z.number().int().min(1, 'Quantidade mínima é 1').max(99),
  notes: optionalText(200),
  optionIds: z.array(z.uuid()).max(40).default([]),
});

export const createOrderSchema = z.object({
  idempotencyKey: z.uuid('Chave de envio inválida'),
  items: z.array(orderItemInputSchema).min(1, 'O carrinho está vazio').max(50, 'Pedido grande demais; divida em partes'),
  customerName: optionalText(40),
  notes: optionalText(300),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

export const cancelItemSchema = z.object({ reason: text(5, 300, 'Descreva o motivo (mínimo 5 caracteres)') });
export const closeSessionSchema = z.object({ reason: optionalText(300) });

export const ORDER_STATUS_LABEL = {
  RECEIVED: 'Recebido',
  IN_PREPARATION: 'Em preparo',
  READY: 'Pronto',
  /** Setor só com impressora: a comanda foi impressa na cozinha/bar. */
  SENT: 'Enviado para o preparo',
  DELIVERED: 'Entregue',
  CANCELLED: 'Cancelado',
} as const;
export type OrderStatusKey = keyof typeof ORDER_STATUS_LABEL;

export const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;

export function minutesToHHMM(m: number) {
  const mm = m % 1440;
  return `${String(Math.floor(mm / 60)).padStart(2, '0')}:${String(mm % 60).padStart(2, '0')}`;
}
export function hhmmToMinutes(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
}

/** Validação de seleção de adicionais — usada no cliente (feedback) e no servidor (regra definitiva). */
export function validateOptionSelection(
  groups: { id: string; name: string; minSelect: number; maxSelect: number; optionIds: string[] }[],
  selected: string[],
): string | null {
  const unique = new Set(selected);
  if (unique.size !== selected.length) return 'Opção repetida';
  const known = new Set(groups.flatMap((g) => g.optionIds));
  for (const id of selected) if (!known.has(id)) return 'Opção inválida para este produto';
  for (const g of groups) {
    const n = g.optionIds.filter((id) => unique.has(id)).length;
    if (n < g.minSelect) return g.minSelect === 1 ? `Escolha uma opção em "${g.name}"` : `Escolha pelo menos ${g.minSelect} em "${g.name}"`;
    if (n > g.maxSelect) return `Escolha no máximo ${g.maxSelect} em "${g.name}"`;
  }
  return null;
}
